import { ApolloClient, useApolloClient, useMutation } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import type { BestPracticeData } from '@deltek/specification-client/core/Models/bestPractice'
import {
    AddUpdateBestPracticeMutation,
    RemoveBestPracticeMutation,
} from 'api/graphQL/mutations/bestPractices.mutations'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import {
    bpStagedConfig,
    requestBPManagementPanelOpen,
    requestBPRemoveConfirmation,
} from 'api/graphQL/reactiveVars/workspaceToggles'
import { useContentItemElements } from 'components/WorkspaceEditor/components/TreeView/hooks/useContentItemElements'
import { updateBestPracticesForContentItem } from 'components/WorkspaceEditor/features/bestPractices'
import { useNotification } from 'components/WorkspaceEditor/services/notificationService'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import {
    useSelectedProjectElementNode,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useContext } from 'react'
import { isSmartProjectCopyEnabled } from 'shared/utils/bestPracticeUtils'
import { BP_MODE } from '../constants'
import {
    BPStagedConfig,
    parameterConfig,
} from '../types/bpManagement.types'
import { extractFamilyNumberFromTreePath } from '../utils/bestPracticesUtils'
import {
    buildFirmBPParameters,
    FirmDataShape,
    FirmModifierLookup,
} from '../utils/firmBPParameterUtils'
import type { BPMenuOpenParams } from './useBestPracticesMenu'

type ContentItemNode = Partial<ContentItemViewNodesType>
type SelectedProjectElementNode = ReturnType<
    typeof useSelectedProjectElementNode
>

const hasAnyParameterSelection = (config: BPStagedConfig): boolean => {
    if (config.projectGroup) return true
    return parameterConfig.some(
        ({ key }) =>
            key !== 'projectGroup' && config.parameterSelected[key].length > 0
    )
}

const buildProjectParametersInput = (
    config: BPStagedConfig,
    lookup: FirmModifierLookup
): { modifierListId: string; selectedModifierIds: string[] }[] => {
    const result: { modifierListId: string; selectedModifierIds: string[] }[] =
        []
    parameterConfig.forEach(({ key }) => {
        if (key === 'projectGroup') return
        const selectedNames = config.parameterSelected[key]
        if (selectedNames.length === 0) return
        const entry = lookup[key]
        if (!entry) return
        const ids = selectedNames
            .map((name) => entry.modifiers[name])
            .filter(Boolean)
        if (ids.length > 0) {
            result.push({
                modifierListId: entry.modifierListId,
                selectedModifierIds: ids,
            })
        }
    })
    return result
}

/** Locate the matching node in the workspace store (handles hyperlink pills). */
const findContentItemNode = (
    nodes: ContentItemNode[],
    params: BPMenuOpenParams
): ContentItemNode | undefined => {
    if (!params.contentItemId) return undefined
    const isHyperLink = params.contentItemType?.toLowerCase() === 'hyperlink'
    return nodes.find((item) =>
        isHyperLink
            ? item.pillId === params.contentItemId
            : item.id === params.contentItemId
    )
}

/**
 * Read the existing BP id from the server when the store says one exists.
 * Skips the network round-trip when the store already shows no BP. Returns
 * undefined on read failure (treated as "no existing BP").
 */
const getExistingBestPracticeId = async (
    apolloClient: ApolloClient<unknown>,
    params: BPMenuOpenParams,
    storeShowsExistingBP: boolean
): Promise<string | undefined> => {
    if (!storeShowsExistingBP) return undefined
    try {
        const { data } = await apolloClient.query<{
            getBestPractice: BestPracticeData | null
        }>({
            query: BestPracticeQuery,
            variables: {
                parentId: params.contentItemId,
                projectId: params.projectId,
            },
            fetchPolicy: 'no-cache',
        })
        return data?.getBestPractice?.id
    } catch {
        return undefined
    }
}

interface BuildMutationInputArgs {
    params: BPMenuOpenParams
    contentItemNode: ContentItemNode | undefined
    selectedProjectElementNode: SelectedProjectElementNode
    getElementsForNode: (id: string) => unknown
    getRecursiveElementsForNode: (id: string) => unknown
}

/**
 * Build the shared portion of the AddUpdateBestPractice mutation input —
 * mirrors BestPracticesSettingPopover so the BE sees the same payload as the
 * right-click "Apply" path.
 */
const buildBPMutationInput = ({
    params,
    contentItemNode,
    selectedProjectElementNode,
    getElementsForNode,
    getRecursiveElementsForNode,
}: BuildMutationInputArgs): Record<string, unknown> => {
    const isManufacturerPill =
        params.elementId !== undefined &&
        (contentItemNode?.isManufacturers || contentItemNode?.isBOD)

    const input: Record<string, unknown> = {
        parentProjectId: params.projectId,
    }
    if (isManufacturerPill) {
        input.listingId = params.elementId
        input.familyNumber = extractFamilyNumberFromTreePath(
            selectedProjectElementNode?.parentTreePath
        )
        input.productType = selectedProjectElementNode?.text
        input.projectElementNodeId = selectedProjectElementNode?.id
    } else {
        input.contentItemId = params.contentItemId
        input.contentItemElementId = params.elementId
        input.projectElementNodeId = selectedProjectElementNode?.id
    }
    if (isSmartProjectCopyEnabled()) {
        if (!params.elementId) {
            const isUserAdded = contentItemNode?.isUserAdded ?? false
            input.elements = isUserAdded
                ? getRecursiveElementsForNode(params.contentItemId)
                : getElementsForNode(params.contentItemId)
            input.isUserAddedContentBestPractice = isUserAdded
        }
        input.isHidden = contentItemNode?.isHidden ?? false
    }
    return input
}

/**
 * Apply the optimistic BP update to the workspace store and return a revert
 * callback. The caller invokes the revert if the mutation fails.
 */
const applyOptimisticBPUpdate = (
    params: BPMenuOpenParams,
    config: BPStagedConfig,
    willRemove: boolean
): (() => void) => {
    const storeState = useWorkspace2Store.getState()
    const previousNodes = storeState.contentItemViewNodes
    const willHaveParameterBP =
        !willRemove && config.mode === BP_MODE.PARAMETER
    const willHaveGlobalBP = !willRemove && config.mode === BP_MODE.GLOBAL
    const updatedNodes = updateBestPracticesForContentItem(
        params.contentItemId,
        params.elementId,
        previousNodes,
        willHaveParameterBP,
        willHaveGlobalBP
    )
    storeState.actions.setContentItemViewNodes(updatedNodes)
    return () => {
        useWorkspace2Store
            .getState()
            .actions.setContentItemViewNodes(previousNodes)
    }
}

/**
 * Returns a function that applies (or removes) the BP Management panel's
 * staged config to a content item, intended for double-click handlers on
 * tree nodes and inline pills.
 */
export const useApplyBPFromConfig = () => {
    const apolloClient = useApolloClient()
    const { currentUserActiveFirm } = useContext(AppBarContext)
    const firmId = currentUserActiveFirm?.firmId
    const selectedProjectElementNode = useSelectedProjectElementNode()
    const {
        getRecursiveElementsForNode,
        getElementsForNode,
    } = useContentItemElements()
    const notify = useNotification()

    const [addUpdateBestPractice] = useMutation(AddUpdateBestPracticeMutation, {
        onCompleted: () => notify.success({ message: 'Best practice applied' }),
        onError: () =>
            notify.error({
                message: 'Failed to apply best practice. Please try again.',
            }),
    })

    const [removeBestPractice] = useMutation(RemoveBestPracticeMutation, {
        onCompleted: () => notify.success({ message: 'Best practice removed' }),
        onError: () =>
            notify.error({
                message: 'Failed to remove best practice. Please try again.',
            }),
    })

    return useCallback(
        async (params: BPMenuOpenParams) => {
            requestBPManagementPanelOpen()
            const config = bpStagedConfig()

            const canAdd =
                config.mode === BP_MODE.GLOBAL ||
                (config.mode === BP_MODE.PARAMETER &&
                    hasAnyParameterSelection(config))

            const initialState = useWorkspace2Store.getState()
            const contentItemNode = findContentItemNode(
                initialState.contentItemViewNodes,
                params
            )
            const storeShowsExistingBP = Boolean(
                contentItemNode?.isBestPractice ||
                    contentItemNode?.isGlobalBestPractice
            )

            const existingId = await getExistingBestPracticeId(
                apolloClient,
                params,
                storeShowsExistingBP
            )

            if (!existingId && !canAdd) {
                notify.info({
                    message:
                        'Select a configuration in the Best Practice Management panel to apply a Best Practice',
                })
                return
            }

            if (existingId) {
                const confirmed = await requestBPRemoveConfirmation()
                if (!confirmed) return
            }

            notify.loading({
                message: existingId
                    ? 'Removing best practice...'
                    : 'Applying best practice...',
            })

            const willRemove = Boolean(existingId)
            const revertOptimistic = applyOptimisticBPUpdate(
                params,
                config,
                willRemove
            )

            if (existingId) {
                removeBestPractice({
                    variables: { input: { id: existingId } },
                }).catch(revertOptimistic)
                return
            }

            // Past this point existingId is falsy and canAdd must be truthy
            // (the early-return above eliminated the !existingId && !canAdd case).

            const commonInput = buildBPMutationInput({
                params,
                contentItemNode,
                selectedProjectElementNode,
                getElementsForNode,
                getRecursiveElementsForNode,
            })

            if (config.mode === BP_MODE.GLOBAL) {
                addUpdateBestPractice({
                    variables: {
                        input: { ...commonInput, isGlobal: true },
                    },
                }).catch(revertOptimistic)
                return
            }

            const firmCacheData = firmId
                ? apolloClient.readQuery<FirmDataShape>({
                      query: FirmProjectParametersQuery,
                      variables: { firmId },
                  })
                : null
            const { lookup } = buildFirmBPParameters(firmCacheData)
            const projectParameters = buildProjectParametersInput(
                config,
                lookup
            )

            // BE requires at least one parameter group or a project group.
            if (projectParameters.length === 0 && !config.projectGroup) {
                revertOptimistic()
                return
            }

            addUpdateBestPractice({
                variables: {
                    input: {
                        ...commonInput,
                        isGlobal: false,
                        projectParameters,
                        projectGroup: config.projectGroup,
                    },
                },
            }).catch(revertOptimistic)
        },
        [
            apolloClient,
            addUpdateBestPractice,
            removeBestPractice,
            firmId,
            selectedProjectElementNode,
            getRecursiveElementsForNode,
            getElementsForNode,
            notify,
        ]
    )
}
